import React, {useEffect, useState} from 'react';
import {Navigation} from 'components/UIComponents/Navigation';
import {ReactComponent as CloseIcon} from 'components/Icons/close.svg';
import {ReactComponent as PrinterIcon} from 'components/Icons/printer.svg';
import {FormattedMessage} from 'react-intl';
import {hasFormatEditor, MenuDocument} from "./MenuDocumentModel";
import {To} from "react-router";
import {SubTitle} from "components/UIComponents/SubTitle";
import {SecondaryButton} from "components/UIComponents/SecondaryButton";
import {WinePicker} from "../winepicker/WinePicker";
import {WineScannerPicker} from "../winescanner/WineScannerPicker";
import {useAppDispatch, useAppSelector} from "lib/redux/hooks";
import {AddWineCategoryAction, menuEditorActions, menuEditorSelectors} from "./editorSlice";
import {AddDropDown} from "./AddDropDown";
import {algoliaWineToEditorWine, isLeafletDocument} from './editorUtils';
import {EditorPreview} from './preview/EditorPreview';
import {EditorSidebar} from "./sidebar/EditorSidebar";
import {AlgoliaWine, algoliaWineCategorySortOrder, getCategory} from "../winepicker/winePickerModel";
import {groupBy, isEmpty, reduceBy, sortBy} from "ramda";
import {DeletePageModal} from "./DeletePageModal";
import PDFPreview from 'features/pdf/PDFPreview';
import {PrimaryButton} from "components/UIComponents/PrimaryButton";
import {PrintModal} from './PrintModal';
import {ConfirmSaveModal} from './ConfirmSaveModal';
import {generateWineMenuPdf} from "../pdf/generateWineMenuPdf";
import Div100vh from "react-div-100vh";
import {IconLabel} from 'components/UIComponents/IconLabel';
import {ErrorBoundary} from "components/UIComponents/ErrorBoundary";
import {Spinner} from "components/UIComponents/Spinner";
import {useDebounced} from "lib/useDebounced";
import {GeneratedPdfResponse} from "../../lib/api/pdfGenerator";
import {AxiosResponse} from "axios";
import {Language} from "../i18n/translations";
import {i18nSelectors} from "../i18n/i18nSlice";
import { createDocumentHandler } from './documentHandlerUtils';
import { labelEditorActions } from 'features/label-editor/labelEditorSlice';
import { AperitifPicker } from 'features/aperitif/AperitifPicker';
import { AperitifPreset } from 'lib/api/aperitifMenu';
import { AperitifDocumentHandler } from './DocumentHandler';

export const ItemTypes = {
    WINE: 'wine'
}

export interface EditorProps {
    menuName: string
    document: MenuDocument
    saveInProgress: boolean
    onDocumentSave: (doc: MenuDocument) => void
    backUrl: To
}

export default function Editor(props: EditorProps) {
    const dispatch = useAppDispatch();

    const initialDocument = props.document;
    useEffect(() => {
        dispatch(menuEditorActions.setDocument(initialDocument));
    }, [dispatch, initialDocument]);

    useEffect(()=>{
        dispatch(menuEditorActions.setFocusedItem(null));
        dispatch(menuEditorActions.updateItemSizesDistributionAndFocus());
    }, [dispatch]);

    useEffect(()=>{
        const beforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            if (e.defaultPrevented) {
                return (e.returnValue = '');
            }
        };
        window.addEventListener('beforeunload', beforeUnload);
        return () => {
            window.removeEventListener('beforeunload', beforeUnload);
        };
    }, []);

    const appLanguage = useAppSelector(i18nSelectors.language);
    const document = useAppSelector(menuEditorSelectors.document);

    // make sure save is always called with latest version of document even debounced
    const _onDocumentSave = props.onDocumentSave;
    const [debouncedSave, immediateSave] = useDebounced(5000, _onDocumentSave, document);

    const winePickerActive = useAppSelector(menuEditorSelectors.winePickerActive);
    const setWinePickerActive = (active: boolean) => dispatch(menuEditorActions.setWinePickerActive(active));

    const wineScannerPickerActive = useAppSelector(menuEditorSelectors.wineScannerPickerActive);
    const setWineScannerPickerActive = (active: boolean) => dispatch(menuEditorActions.setWineScannerPickerActive(active));

    const aperitifPickerActive = useAppSelector(menuEditorSelectors.aperitifPickerActive);
    const setAperitifPickerActive = (active: boolean) => dispatch(menuEditorActions.setAperitifPickerActive(active));

    const addAperitifPickerItems = (presets: AperitifPreset[]) => {
        if (document == null) return;
        const handler = createDocumentHandler(document);
        if (handler instanceof AperitifDocumentHandler) {
            handler.handleAddAperitifs(presets, dispatch);
            debouncedSave();
        }
    };

    const addWinePickerWines = (algoliaWines: AlgoliaWine[]) => {
        if (document == null) {
            return;
        }
        createDocumentHandler(document)
            .handleAddWines(algoliaWines, dispatch);
        debouncedSave();
    }

    const formatOptionsActive = useAppSelector(menuEditorSelectors.formatOptionsActive);
    const toggleFormatOptionsActive = () => dispatch(menuEditorActions.setFormatOptionsActive(!formatOptionsActive));

    const [pdf, setPdf] = useState<string | null>(null);
    const [pdfError, setPdfError] = useState<boolean>(false);
    const [previewOpen, setPreviewOpen] = useState<boolean>(false);

    const getPDF = () => {
        immediateSave();
        setPdfError(false);
        setPdf(null);

        if (document == null) {
            return;
        }

        generateWineMenuPdf(document)
            .then((response: AxiosResponse<GeneratedPdfResponse>) => {
                setPdf(response.data.url);
                setPdfError(false);
            })
            .catch(function (error) {
                setPdf(null);
                setPdfError(true);
            });
    };

    const openConfirmSaveModal = () => dispatch(menuEditorActions.setConfirmSavePopover(true));

    useEffect(()=>{
        const isLeaflet = document&&isLeafletDocument(document);
        if (isLeaflet && document.pages.length === 0) {
            setWinePickerActive(true);
        }
    }, [document]); // eslint-disable-line

    const addLabel = () => {
        dispatch(labelEditorActions.addNewLabelItem());
    };

    return (
        <Div100vh className="flex flex-col items-stretch w-screen">
            {winePickerActive && (
                <div className="fixed inset-0 z-modal">
                    <WinePicker
                        onClose={() => setWinePickerActive(false)}
                        onPick={addWinePickerWines}
                        language={document?.language ?? appLanguage ?? 'de'}
                    />
                </div>
            )}
            {wineScannerPickerActive && (
                <div className="fixed inset-0 z-modal">
                    <WineScannerPicker
                        onClose={() => setWineScannerPickerActive(false)}
                        onPick={addWinePickerWines}
                        language={document?.language ?? appLanguage ?? 'de'}
                    />
                </div>
            )}
            {aperitifPickerActive && (
                <div className="fixed inset-0 z-modal">
                    <AperitifPicker
                        onClose={() => setAperitifPickerActive(false)}
                        onPick={addAperitifPickerItems}
                        language={document?.language ?? appLanguage ?? 'de'}
                    />
                </div>
            )}
            <ConfirmSaveModal backUrl={props.backUrl}/>
            <DeletePageModal/>
            <PrintModal/>
            {previewOpen &&
              <div className="fixed inset-0 z-modal">
                <ErrorBoundary>
                  <PDFPreview
                    open={previewOpen}
                    pdf={pdf}
                    error={pdfError}
                    onClose={() => setPreviewOpen(false)}
                  />
                </ErrorBoundary>
              </div>
            }
            <div className="flex-none">
                <Navigation
                    left={
                        <button
                            onClick={openConfirmSaveModal}
                            className="flex items-center gap-5"
                        >
                            <CloseIcon className="h-5"/>
                            <SubTitle>{props.menuName}</SubTitle>
                        </button>
                    }
                    right={<>
                        {(document?.type==='wine' || document?.type==='leaflet' || document?.type==='aperitif')
                            ? <AddDropDown/>
                            : null
                        }
                        {document?.type==='label' && <SecondaryButton
                            onClick={addLabel}
                        >
                            <FormattedMessage id="editor.label.addLabel" />
                        </SecondaryButton>}
                        {document?.type&&hasFormatEditor(document.type)&&<SecondaryButton
                            pressed={formatOptionsActive}
                            onClick={toggleFormatOptionsActive}>
                            <FormattedMessage id="editor.nav.format"/>
                        </SecondaryButton>}
                        <span></span>
                        <SecondaryButton
                            onClick={() => {
                                setPreviewOpen(true);
                                getPDF();
                            }}
                            className="flex items-center gap-5"
                        >
                            <IconLabel icon={<PrinterIcon className="h-5 w-5"/>}>
                                <FormattedMessage id="editor.nav.printPreview"/>
                            </IconLabel>
                        </SecondaryButton>
                        <span/>
                        <PrimaryButton onClick={immediateSave} disabled={props.saveInProgress}>
                            <div className="relative">
                                <span className={props.saveInProgress ? 'opacity-0' : ''}><FormattedMessage
                                    id="editor.nav.save"/></span>
                                {props.saveInProgress && (
                                    <div className="absolute inset-0 flex justify-center items-center">
                                        <Spinner className="w-4"/>
                                    </div>
                                )}
                            </div>
                        </PrimaryButton>
                    </>}
                />
            </div>

            <div className="flex flex-auto items-stretch border-t-2 border-t-gray-3 overflow-hidden">

                <div className="flex-auto overflow-auto bg-gray-1">
                    <ErrorBoundary>
                        <EditorPreview/>
                    </ErrorBoundary>
                </div>

                <div className="flex-none overflow-auto w-[350px] bg-warm-gray">
                    <ErrorBoundary>
                        <EditorSidebar/>
                    </ErrorBoundary>
                </div>
            </div>
        </Div100vh>
    );
}



export function toAddWineCategoryActions(language: Language, algoliaWines: AlgoliaWine[]): AddWineCategoryAction[] {
    const localizedCategoryNames = reduceBy<AlgoliaWine, string>(
        (acc, algoliaWine) => !isEmpty(acc) ? acc : getCategory(language, algoliaWine) ?? "",
        "",
        algoliaWine => algoliaWine.category ?? "",
        algoliaWines);
    const winesByCategory = groupBy(w => w.category ?? "", algoliaWines);
    const actions = Object.entries(winesByCategory).map(([category, algoliaWines]) => ({
        category: category,
        wines: sortBy(w => w.country ?? "", algoliaWines).map(algoliaWine => algoliaWineToEditorWine(language, algoliaWine))
    }));
    const sortedActions = sortBy(({category}) => algoliaWineCategorySortOrder(category), actions);
    return sortedActions
        .map(action => ({
            ...action,
            category: localizedCategoryNames[action.category] ?? action.category
        }));
}